import CDATA from './CDATA';
import config from '../config';
import axios from 'axios';
import Auth0SessionInfo from './Auth0SessionInfo';
import CryptoAES from 'crypto-js/aes';
import CryptoEnc from 'crypto-js/enc-utf8';

const { encKey } = config.sessionStorage;

const getClientCode = () => {
  const appStateInSession = sessionStorage.getItem('AppStateStorage');
  const appState = appStateInSession && JSON.parse(
    CryptoAES.decrypt(appStateInSession.toString(), encKey).toString(CryptoEnc),
  );
  return appState?.selectedClient?.code ?? appState.availableClientCodeAndNames[0].code;
};

const getReportingPeriod = () => {
  const appStateInSession = sessionStorage.getItem('AppStateStorage');
  const appState = appStateInSession && JSON.parse(
    CryptoAES.decrypt(appStateInSession.toString(), encKey).toString(CryptoEnc),
  );
  return appState?.selectedReportingDate?.code ?? null;
};

// ─── Scopes ──────────────────────────────────────────────────────────

export const getScopes = () =>
  CDATA.makeRequest('GET', 'PRIIPS_dist_scope', '', null, 'Failed to load scopes', null, null, true);

export const deleteScope = (scopeId) =>
  CDATA.makeRequest('DELETE', `PRIIPS_dist_scope(${scopeId})`, '', null, 'Failed to delete scope');

export const duplicateScope = (scope) =>
  CDATA.makeRequest('POST', 'PRIIPS_dist_scope', '', {
    ScopeName: `${scope.ScopeName} - Copy`,
    ScopeId: scope.ScopeId,
  }, 'Failed to duplicate scope');

// ─── Target Profiles ─────────────────────────────────────────────────

export const getTargetProfiles = () =>
  CDATA.makeRequest('GET', 'PRIIPS_dist_TargetProfile', '', null, 'Failed to load target profiles',null,null,true);

export const getTargetProfileDistributionTargetsFtp = () =>
  CDATA.makeRequest('GET', 'PRIIPS_dist_TargetProfileDistributionTargetFtp', '', null, 'Failed to load target profiles',null,null,true);

export const getTargetTypes = () =>
  CDATA.makeRequest('GET', 'PRIIPS_dist_targettype', '', null, 'Failed to load target types', null, null, true);

export const getTargetsFtps = () =>
  CDATA.makeRequest('GET', 'PRIIPS_dist_DistributionTargetFtp', '', null, 'Failed to load FTP targets', null, null, true);

export const getTargetsScopes = () =>
  CDATA.makeRequest('GET', 'PRIIPS_dist_targetscope', '', null, 'Failed to load target scopes', null, null, true);

export const deleteTargetProfile = (targetProfileId) =>
  CDATA.makeRequest('DELETE', `PRIIPS_dist_TargetProfile(${targetProfileId})`, '', null, 'Failed to delete target profile');

export const duplicateTargetProfile = (profile) =>
  CDATA.makeRequest('POST', 'PRIIPS_dist_TargetProfile', '', {
    TargetProfileId: profile.TargetProfileId,
    TargetProfileName: profile.TargetProfileName,
    TargetTypeId: profile.TargetTypeId,
    IsActive: profile.IsActive,
    CreatedDateTime: new Date().toISOString(),
    UpdatedDateTime: new Date().toISOString(),
    ArchiveDocumentFormatKey: profile.ArchiveDocumentFormatKey,
    ArchiveDocumentFormatTypeId: profile.ArchiveDocumentFormatTypeId,
    ArchiveDocumentNamingConvention: profile.ArchiveDocumentNamingConvention,
    IsOneFilePerFund: profile.IsOneFilePerFund,
    IsRestricted: profile.IsRestricted,
    ArchiveAsSingleFile: profile.ArchiveAsSingleFile,
  }, 'Failed to duplicate target profile');

// ─── Target Profile Preview ─────────────────────────────────────────

export const getTargetProfilePreview = (targetProfileId, referenceDate) => {
  const clientCode = getClientCode();
  return CDATA.makeRequest('POST', 'PRIIPS_dist_TargetProfile_previewTargets', '', {
    TargetProfileId: targetProfileId,
    ClientCode: clientCode,
    ReferenceDate: referenceDate,
  }, 'Failed to load target profile preview');
};

export const getTargetProfileSelectedReports = (targetProfileId) =>{
  const clientCode = getClientCode();
  return CDATA.makeRequest('GET', `PRIIPS_dist_targetreport(${targetProfileId})`, '', { ClientCode: clientCode }, 'Failed to load selected reports',null,null,true);
}

// ─── Target Profiles Status ─────────────────────────────────────────

export const getTargetProfilesStatus = (referenceDate) => {
  const clientCode = getClientCode();
  return CDATA.makeRequest('POST', 'PRIIPS_dist_TargetProfile_targetStatus', '', {
    ClientCode: clientCode,
    ReferenceDate: referenceDate,
  }, 'Failed to load target profile statuses');
};

// ─── Distribution (Publish) ──────────────────────────────────────────

export const distributeProfile = async (targetProfileId, partialDistribution) => {
  const clientCode = getClientCode();
  const reportingPeriod = getReportingPeriod();
  const token = Auth0SessionInfo.GetSessionInfo().accessToken;
  const baseURL = config.apim.url;

  const API = axios.create({
    baseURL: `${baseURL}/distribution/DistributeTargetProfile`,
    timeout: 900000,
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
      client_code: clientCode,
    },
  });

  const response = await API.request('', {
    method: 'POST',
    data: {
      TargetProfileId: targetProfileId,
      UserId: 0,
      ClientCode: clientCode,
      ReportPeriod: reportingPeriod,
      PartialDistribution: partialDistribution,
    },
  });

  return response.data;
};

// ─── Scope Detail (ScopePage) ─────────────────────────────────────────

export const getScopeById = (scopeId) =>
  CDATA.makeRequest('GET', `PRIIPS_dist_scope(${scopeId})`, '', null, 'Failed to load scope', null, null, true);

export const createScope = (scope) =>
  CDATA.makeRequest('POST', 'PRIIPS_dist_scope', '', scope, 'Failed to create scope');

export const updateScope = (scope) =>
  CDATA.makeRequest('PUT', `PRIIPS_dist_scope(${scope.ScopeId})`, '', scope, 'Failed to update scope');

export const getScopeFundClasses = (scopeId) =>
  CDATA.makeRequest('GET', `PRIIPS_dist_scopefundclass_byScopeId(${scopeId})`, '', null, 'Failed to load scope fund classes',null,null,true);

export const createScopeFundClass = (scopeFundClass) =>
  CDATA.makeRequest('POST', 'PRIIPS_dist_scopefundclass', '', scopeFundClass, 'Failed to create scope fund class');

export const deleteScopeFundClass = (scopeFundClassId) =>
  CDATA.makeRequest('DELETE', `PRIIPS_dist_scopefundclass(${scopeFundClassId})`, '', null, 'Failed to delete scope fund class');

export const getFundsAndClasses = () => {
  const clientCode = getClientCode();
  return CDATA.makeRequest('GET', `PRIIPS_app_all_funds_and_classes(${clientCode})`, '', null, 'Failed to load funds and classes');
};

// ─── Target Profile Detail (TargetProfilePage) ──────────────────────

export const getTargetProfileById = (profileId) =>
  CDATA.makeRequest('GET', `PRIIPS_dist_TargetProfile(${profileId})`, '', null, 'Failed to load target profile',null,null,true);

export const createTargetProfile = (profile) =>
  CDATA.makeRequest('POST', 'PRIIPS_dist_TargetProfile', '', profile, 'Failed to create target profile',null,null,true);

export const updateTargetProfile = (profile) =>
  CDATA.makeRequest('PUT', `PRIIPS_dist_TargetProfile(${profile.TargetProfileId})`, '', profile, 'Failed to update target profile',null,null,true);

export const getReportTypes = () =>
  CDATA.makeRequest('GET', 'PRIIPS_doc_reporttypes', '', null, 'Failed to load report types', null, null, true);

export const getReportsByProfileId = (profileId) =>
  CDATA.makeRequest('GET', `PRIIPS_dist_targetreport(${profileId})`, '', null, 'Failed to load target reports', null, null, true);

export const getAllReports = () =>
  CDATA.makeRequest('GET', 'PRIIPS_dist_targetreport', '', null, 'Failed to load all reports');

export const createTargetReport = (report) =>
  CDATA.makeRequest('POST', 'PRIIPS_dist_targetreport', '', report, 'Failed to create target report');

export const updateTargetReport = (report) =>
  CDATA.makeRequest('PUT', `PRIIPS_dist_targetreport(${report.TargetReportId})`, '', report, 'Failed to update target report');

export const deleteTargetReport = (reportId) =>
  CDATA.makeRequest('DELETE', `PRIIPS_dist_targetreport(${reportId})`, '', null, 'Failed to delete target report');

export const getAllReportLanguages = () =>
  CDATA.makeRequest('GET', 'PRIIPS_dist_targetreportlanguage', '', null, 'Failed to load report languages', null, null, true);

export const createReportLanguage = (reportLang) =>
  CDATA.makeRequest('POST', 'PRIIPS_dist_targetreportlanguage', '', reportLang, 'Failed to create report language');

export const deleteReportLanguage = (reportLanguageId, isoLanguage) =>
  CDATA.makeRequest('DELETE', `PRIIPS_dist_targetreportlanguage(${reportLanguageId}, ${isoLanguage})`, '', null, 'Failed to delete report language');

export const getNamingConventions = () =>
  CDATA.makeRequest('GET', 'PRIIPS_dist_naming_convention', '', null, 'Failed to load naming conventions', null, null, true);

export const getClientLanguages = () =>
  CDATA.makeRequest('GET', 'PRIIPS_app_clientLanguage', '', null, 'Failed to load client languages', null, null, true);

export const getTargetProfileDistFtps = () =>
  CDATA.makeRequest('GET', 'PRIIPS_dist_TargetProfileDistributionTargetFtp', '', null, 'Failed to load distribution FTPs');

export const getTargetProfileDistEmails = () =>
  CDATA.makeRequest('GET', 'PRIIPS_dist_TargetProfileDistributionTargetEmail', '', null, 'Failed to load distribution emails', null, null, true);

export const deleteTargetScope = (targetProfileId, scopeId) =>
  CDATA.makeRequest('DELETE', `PRIIPS_dist_targetscope(${targetProfileId}, ${scopeId})`, '', null, 'Failed to delete target scope');

export const registerFtp = async (ftpData) => {
  const clientCode = getClientCode();
  const token = Auth0SessionInfo.GetSessionInfo().accessToken;
  const baseURL = config.apim.url;

  const API = axios.create({
    baseURL: `${baseURL}/distribution/RegisterFtp`,
    timeout: 900000,
    headers: {
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
    },
  });

  const response = await API.request('', { method: 'POST', data: ftpData });
  return response.data;
};

// ─── File Import (Upload) ────────────────────────────────────────────

export const uploadDistributionFile = async (file, entity) => {
  const clientCode = getClientCode();
  const token = Auth0SessionInfo.GetSessionInfo().accessToken;
  const baseURL = config.cdata.url;
  const entityPath = entity === 'Scope'
    ? 'DistTargetScopeDocumentIngestion'
    : 'DistTargetProfileDocumentIngestion';

  const formData = new FormData();
  formData.append('file', file);

  const response = await axios.post(
    `${baseURL}/${entityPath}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        authorization: `Bearer ${token}`,
        client_code: clientCode,
      },
      timeout: 900000,
    },
  );

  return response.data;
};
